/**
 * StatusBadge - Color-coded status pill
 *
 * Usage:
 * <StatusBadge status="closed" />
 * <StatusBadge status="no-show" />
 * <StatusBadge status="follow-up" />
 */

interface StatusBadgeProps {
  status: string;
}

// Default status -> color mapping
const statusColors: Record<string, { bg: string; text: string }> = {
  // Positive outcomes
  closed: { bg: "bg-green-500/20", text: "text-green-400" },
  mrr: { bg: "bg-emerald-500/20", text: "text-emerald-400" },
  sale: { bg: "bg-green-500/20", text: "text-green-400" },
  won: { bg: "bg-green-500/20", text: "text-green-400" },

  // Negative outcomes
  "no-show": { bg: "bg-red-500/20", text: "text-red-400" },
  "no show": { bg: "bg-red-500/20", text: "text-red-400" },
  cancelled: { bg: "bg-red-500/20", text: "text-red-400" },
  lost: { bg: "bg-red-500/20", text: "text-red-400" },

  // Neutral/pending
  "follow-up": { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  "follow up": { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  "follow-up scheduled": { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  pending: { bg: "bg-yellow-500/20", text: "text-yellow-400" },
  scheduled: { bg: "bg-blue-500/20", text: "text-blue-400" },

  // Special
  downsold: { bg: "bg-orange-500/20", text: "text-orange-400" },
  downsell: { bg: "bg-orange-500/20", text: "text-orange-400" },
  applicant: { bg: "bg-purple-500/20", text: "text-purple-400" },
  optin: { bg: "bg-sky-500/20", text: "text-sky-400" },
  "opt-in": { bg: "bg-sky-500/20", text: "text-sky-400" },
};

// Default colors for unknown status
const defaultColor = { bg: "bg-gray-500/20", text: "text-gray-400" };

export function StatusBadge({ status }: StatusBadgeProps) {
  const normalizedStatus = status.toLowerCase().trim();
  const colors = statusColors[normalizedStatus] || defaultColor;

  // Capitalize first letter for display
  const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      {displayStatus}
    </span>
  );
}
